
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { Language, KnownAllergyId } from '../types';
import XIcon from './icons/XIcon';
import FilterChip from './FilterChip';
import { getTranslation, isRTL } from '../utils/translations';
import { ALLERGY_CONFIG, toggleKnownAllergy } from '../utils/allergies';

interface AllergyFilterPickerModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedAllergens: KnownAllergyId[];
    onApply: (allergens: KnownAllergyId[]) => void;
    language?: Language;
}

const AllergyFilterPickerModal: React.FC<AllergyFilterPickerModalProps> = ({
    isOpen, onClose, selectedAllergens, onApply, language = 'en',
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    const rtl = isRTL(language);
    const textDir = rtl ? 'rtl' : 'ltr';

    const [selected, setSelected] = useState<KnownAllergyId[]>(selectedAllergens);

    useEffect(() => {
        if (isOpen) {
            setSelected(selectedAllergens);
        }
    }, [isOpen, selectedAllergens]);

    if (!isOpen) return null;

    const handleApply = () => {
        onApply(selected);
        onClose();
    };

    return createPortal(
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[510] flex items-center justify-center p-4 animate-fade-in"
            onClick={onClose}
        >
            <div
                className="relative bg-white dark:bg-slate-900 w-full max-w-md max-h-[min(85dvh,calc(100dvh-2rem))] rounded-[2.5rem] shadow-2xl flex flex-col overflow-hidden border border-white/20 dark:border-slate-800 transition-colors"
                dir={textDir}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sticky top-0 z-20 flex justify-between items-center p-4 border-b border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0">
                    <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 text-start min-w-0 pe-2">
                        {t('allergyFilterPickerTitle')}
                    </h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-8 h-8 rounded-full bg-gray-50 dark:bg-slate-800 text-gray-500 dark:text-slate-400 flex items-center justify-center shrink-0"
                        aria-label={t('close')}
                    >
                        <XIcon className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto p-4 pb-24 scrollbar-none">
                    <div className="flex flex-row flex-wrap gap-2">
                        {ALLERGY_CONFIG.map(({ id, labelKey, emoji }) => (
                            <FilterChip
                                key={id}
                                label={`${emoji} ${t(labelKey as Parameters<typeof getTranslation>[1])}`}
                                isActive={selected.includes(id)}
                                onClick={() => setSelected((prev) => toggleKnownAllergy(prev, id))}
                            />
                        ))}
                    </div>
                </div>

                <div className="absolute bottom-0 w-full p-4 pb-[max(1rem,env(safe-area-inset-bottom))] bg-white dark:bg-slate-900 border-t border-gray-100 dark:border-slate-800 flex justify-between items-center gap-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-20">
                    <button
                        type="button"
                        onClick={() => setSelected([])}
                        className="text-slate-500 dark:text-slate-400 font-medium text-sm hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                    >
                        {t('clearAll')}
                    </button>
                    <button
                        type="button"
                        onClick={handleApply}
                        className="bg-brand-orange text-white px-8 py-3 rounded-xl font-bold text-sm hover:opacity-90 active:scale-95 transition-all shadow-lg shadow-brand-orange/20"
                    >
                        {t('save')}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default AllergyFilterPickerModal;
